import { useMediaQuery } from '@mui/material';
import { common } from '@mui/material/colors';
import { alpha, Breakpoint, createTheme } from '@mui/material/styles';
import { useTheme } from '@mui/styles';

export const BREAKPOINTS_VALUES = {
  xs: 0,
  sm: 500,
  md: 800,
  lg: 1100,
  xl: 1200,
  ml: 2200,
};

export const RESTRICTED_CONTAINER_WIDTH = 1600;

export const colorsPalette = {
  black: '#00000066',
  blackPure: '#000000',
  blackSecondary: '#23202E',
  white: '#ffffff',
  whiteSecondary: 'rgba(255,255,255,.15)',
  gray: '#C9C4D4',
  graySecondary: '#F2F4F5',
  graySaturated: '#828282',
  grayLight: '#c8c3d4',
  grayThird: '#BEBAC9',
  blue: '#6869ED',
  blueDark: '#1b1925',
  blueLight: '#20A4F3',
  violet: '#8686f1',
  violetSecondary: '#a2a4ee',
  violetDark: '#22202D',
  violetAnalyticsToggle: '#4C4663',
  violetAnalyticsCard: '#2D293B',
  iconViolet: '#353359',
  turquoiseLight: '#a6fff6',
  red: '#ff0000',
  blueProgressBar: '#5A6EED',
  primary: '#7EFCF0',
  pink: '#E5BFEE',
  green: '#86E380',
  error: '#CA062E',
  errorLight: '#FB6382',
  failedError: '#D86B67',
  loadingAnimation: '#49445E',
  loadingAnimationLight: '#565070',
  yellow: '#E6FB7A',
  coral: '#F45B69',
  linkedInBlue: '#0077B7',
  transparent: 'transparent',
};

export const layers = {
  stickyFooter: 100,
};

export const INNER_BOTTOM_SPACE_DESK = 36;

export const TINY_MARGIN = 8;
export const SMALL_MARGIN = 18;
export const SUBMIDDLE_MARGIN = 24;
export const MIDDLE_MARGIN = 36;
export const BIG_MARGIN = 54;
export const LARGE_MARGIN = 72;

export const TINY_MARGIN_PX = TINY_MARGIN + 'px';
export const SMALL_MARGIN_PX = SMALL_MARGIN + 'px';
export const SUBMIDDLE_MARGIN_PX = SUBMIDDLE_MARGIN + 'px';
export const MIDDLE_MARGIN_PX = MIDDLE_MARGIN + 'px';
export const BIG_MARGIN_PX = BIG_MARGIN + 'px';
export const LARGE_MARGIN_PX = LARGE_MARGIN + 'px';

export const MIDDLE_RADIUS = 8;
export const BIG_RADIUS = 16;

export const SMALL_RADIUS_PX = '5px';
export const MIDDLE_RADIUS_PX = MIDDLE_RADIUS + 'px';
export const BIG_RADIUS_PX = BIG_RADIUS + 'px';

const SKELETON_TEXT_HEIGHT_TINY = 18;
const SKELETON_TEXT_HEIGHT_SMALL = 24;
const SKELETON_TEXT_HEIGHT = 36;
const SKELETON_TEXT_HEIGHT_BIG = 54;
export const SKELETON_TEXT_HEIGHT_TINY_PX = SKELETON_TEXT_HEIGHT_TINY + 'px';
export const SKELETON_TEXT_HEIGHT_SMALL_PX = SKELETON_TEXT_HEIGHT_SMALL + 'px';
export const SKELETON_TEXT_HEIGHT_PX = SKELETON_TEXT_HEIGHT + 'px';
export const SKELETON_TEXT_HEIGHT_BIG_PX = SKELETON_TEXT_HEIGHT_BIG + 'px';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      light: '#7EFCF0',
      main: '#7EFCF0',
      dark: '#7EFCF0',
      contrastText: common.black,
    },
    secondary: {
      light: '#23202E',
      main: '#23202E',
      dark: '#1B1925',
      contrastText: common.white,
    },
  },
  typography: {
    fontFamily: '"sofia-pro", sans-serif',
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
  },
  breakpoints: { values: BREAKPOINTS_VALUES },
  components: {
    MuiContainer: {
      styleOverrides: {},
    },
  },
});

const components = {
  MuiContainer: {
    root: {
      [theme.breakpoints.up('sm')]: {
        paddingLeft: 16,
        paddingRight: 16,
      },
      [theme.breakpoints.up('md')]: {
        paddingLeft: 24,
        paddingRight: 24,
      },
      [theme.breakpoints.up('xl')]: {
        paddingLeft: 16,
        paddingRight: 16,
      },
    },
    maxWidthLg: {
      [theme.breakpoints.up('sm')]: {
        maxWidth: 1200,
      },
    },
  },
  MuiAlert: {
    filledSuccess: {
      color: 'rgba(0, 0, 0, 0.87)',
      backgroundColor: '#7EFCF0',
    },
    filledError: {
      backgroundColor: '#f44336',
    },
  },
  MuiTabs: {
    flexContainer: {
      borderBottom: '2px solid rgba(134, 134, 241, 0.301961)',
    },
    indicator: {
      backgroundColor: `${colorsPalette.violet}`,
    },
  },
  MuiTab: {
    root: {
      minWidth: 0,
      marginRight: 15,
      fontSize: 20,
      fontWeight: 'bold',
      textTransform: 'none',

      [theme.breakpoints.up('sm')]: {
        minWidth: 0,
      },

      '&.Mui-selected': {
        color: colorsPalette.white,
      },
    },
  },
  MuiSelect: {
    select: {
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },
  },
  MuiList: {
    root: {
      background: colorsPalette.violetAnalyticsCard,
    },
  },
  MuiOutlinedInput: {
    root: {
      borderRadius: 8,
      borderColor: alpha(colorsPalette.white, 0.33),
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: colorsPalette.blue,
        borderWidth: 1,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: colorsPalette.blue,
      },
      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: colorsPalette.errorLight,
      },
    },
    input: {
      color: colorsPalette.gray,
      fontSize: 18,
      lineHeight: '26px',
      '&:-webkit-autofill': {
        color: colorsPalette.gray,
        '-webkit-box-shadow': `0 0 0 100px ${colorsPalette.violetAnalyticsCard} inset`,
      },
    },
    notchedOutline: {
      '& legend': {
        fontSize: 14,
      },
    },
    adornedEnd: {
      paddingRight: 7,
    },
  },
  MuiInputLabel: {
    root: {
      color: alpha(colorsPalette.white, 0.33),
      fontSize: 18,
      lineHeight: '26px',
      '&.Mui-focused': {
        color: colorsPalette.blue,
      },
      '&.Mui-error': {
        color: colorsPalette.error,
      },
    },
    outlined: {
      transform: 'translate(14px, 16px) scale(1)',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -10px) scale(0.75)',
      },
    },
  },
  MuiFormHelperText: {
    root: {
      '&.Mui-error': {
        color: colorsPalette.error,
      },
    },
  },
  MuiAutocomplete: {
    listbox: {
      '& .MuiAutocomplete-option.Mui-focused': {
        backgroundColor: colorsPalette.violetAnalyticsCard,
      },
    },
    tag: {
      borderRadius: 16,
    },
    clearIndicator: {
      display: 'none',
    },
    popupIndicator: {
      color: alpha(colorsPalette.white, 0.33),
    },
    popupIndicatorOpen: {
      color: colorsPalette.blue,
    },
    paper: {
      margin: 0,
      backgroundColor: colorsPalette.blackSecondary,
      backgroundImage: 'none',
    },
    option: {
      fontSize: 18,
      lineHeight: '26px',
      color: colorsPalette.gray,
      padding: '16px 8px',
      '&.Mui-focused': {
        backgroundColor: colorsPalette.violetAnalyticsCard,
      },
    },
  },
  MuiCalendarPicker: {
    root: {
      backgroundColor: [theme.palette.secondary.light],
      '& > div:first-child': {
        padding: 0,
        '& > div:first-child': {
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          pointerEvents: 'none',
          '& .MuiButtonBase-root': {
            display: 'none',
          },
        },
        '& > div:last-child': {
          width: '100%',
          justifyContent: 'space-between',
          '& .MuiIconButton-root': {
            background: colorsPalette.violetAnalyticsCard,
            '&:hover': {
              background: alpha(colorsPalette.white, 0.08),
            },
            '& .Mui-disabled': {
              background: 'transparent',
            },
          },
        },
      },
      '& .PrivatePickersSlideTransition-root ': {
        minHeight: 240,
      },
    },
  },
  MuiPickersBasePicker: {
    pickerView: {
      backgroundColor: [theme.palette.secondary.light],
    },
  },
  MuiPickersCalendarHeader: {
    iconButton: {
      backgroundColor: colorsPalette.violetAnalyticsCard,
    },
  },
  MuiPickersDay: {
    root: {
      fontSize: 14,
      backgroundColor: [theme.palette.secondary.light],
      '&:hover': {
        backgroundColor: colorsPalette.violetAnalyticsCard,
      },
      '&.Mui-selected': {
        backgroundColor: colorsPalette.primary,
      },
    },
    today: {
      border: 'none !important',
      color: colorsPalette.primary,
      '&:hover': {
        backgroundColor: colorsPalette.violetAnalyticsCard,
      },
      '&:focus': {
        color: colorsPalette.blackPure,
        backgroundColor: colorsPalette.primary,
      },
    },
  },
  MuiAccordion: {
    root: {
      backgroundImage: 'none',
    },
  },
  MuiLink: {
    root: {
      textDecoration: 'none',
    },
  },
  MuiButton: {
    root: {
      '&:hover': {
        background: alpha(colorsPalette.white, 0.15),
      },
    },
  },
  MuiIconButton: {
    root: {
      padding: 12,
    },
  },
};

for (const tKey in components) {
  if (theme.components) {
    theme.components[tKey] = {};
    theme.components[tKey].styleOverrides = components[tKey];
  }
}

theme.typography.h1 = {
  fontSize: 32,
  lineHeight: '40px',
  fontWeight: 'bold',
  marginBottom: 0,
  [theme.breakpoints.up('sm')]: {
    fontSize: 40,
    lineHeight: 1.2,
    marginBottom: 0,
  },
};

theme.typography.h2 = {
  fontSize: 24,
  lineHeight: '29px',
  fontWeight: 'bold',
  marginBottom: 22,
  [theme.breakpoints.up('sm')]: {
    fontSize: 28,
    lineHeight: 1.2,
    marginBottom: 0,
  },
};

theme.typography.h3 = {
  fontSize: 18,
  lineHeight: '22px',
  fontWeight: 'bold',
  marginBottom: 8,
  [theme.breakpoints.up('sm')]: {
    fontSize: 24,
    lineHeight: 1.2,
    marginBottom: 0,
  },
};

theme.typography.h4 = {
  fontSize: 22,
  lineHeight: '22px',
  fontWeight: 'bold',
  marginBottom: 0,
  [theme.breakpoints.up('sm')]: {
    fontSize: 20,
    lineHeight: '22px',
    marginBottom: 0,
  },
};

export function useIsWidthUp(breakpoint) {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.up(breakpoint));
}

export function useIsWidthBetween(start, end) {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.between(start, end));
}

export function useWidth(defaultWidth: Breakpoint = 'sm') {
  const theme = useTheme();
  const keys = [...theme.breakpoints.keys].reverse();
  const width: Breakpoint =
    keys.reduce((output: null | Breakpoint, key) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const matches = useMediaQuery(theme.breakpoints.up(key));
      return !output && matches ? key : output;
    }, null) || defaultWidth;

  return width;
}

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    ml: true;
  }
}

export default theme;
